import React, { useState, useEffect, useRef } from 'react';
import { evaluateExpression, shouldEvaluate } from '../utils/expression-evaluator/index';

function normalizeValue(locale, numberValue) {
    if (numberValue === null || numberValue === undefined || numberValue === '') {
        return NaN;
    }
    if (typeof numberValue === 'number') {
        return numberValue;
    }

    let cleanedValue = String(numberValue).replace(/\s+/g, '');
    cleanedValue = locale === 'en-EN' ? cleanedValue.replace(/,/g, '') : cleanedValue.replace(/,/g, '.');

    return parseFloat(cleanedValue);
}

const formatMoney = (value, locale) => {
  if (value === null || value === undefined || value === '') return '';
  
  let numberValue = value;

  numberValue = normalizeValue(locale, numberValue);
  if (isNaN(numberValue)) return '';

    return `$ ${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numberValue)}`;
};

const MoneyInput = ({ value, onChange, locale = 'en-EN', placeholder = "", required=false, className = "", isReadOnly=false }) => {
  const [inputValue, setInputValue] = useState(formatMoney(value ?? '', locale));
  const [selected, setSelected] = useState(false);
  const inputRef = useRef(null);  // Add ref to input
  const lastFormattedValueRef = useRef(formatMoney(value ?? '', locale));
  const lastValidValueRef = useRef(value ?? null);


  const handleInputChange = (e) => {
    let { value } = e.target;

    if(locale === "fr-FR")
        value = value.replace(/,/g, '.')

    // Remove any characters that are not allowed in mathematical expressions
    // Allow: digits, operators (+, -, *, /, ^, %), parentheses, spaces, commas, decimal point, and letters (for function names like sqrt, mod, abs, min, max)
    value = value.replace(/[^0-9.+*/\-()^%\s,a-zA-Z]/g, '');

    setInputValue(value);
  };

  // Sync inputValue with value prop when it changes
  useEffect(() => {
    const formattedValue = formatMoney(value ?? '', locale);
    setInputValue(formattedValue);
    lastFormattedValueRef.current = formattedValue;
    lastValidValueRef.current = value ?? null;
  }, [value, locale]);

  const handleBlur = () => {
    setSelected(false)
    // Try to evaluate mathematical expression if present
    let evaluatedValue = inputValue;
    // Check if input contains mathematical operators
    if (shouldEvaluate(inputValue)) {
      const result = evaluateExpression(inputValue);
      if (result !== null && !isNaN(result) && isFinite(result)) {
        evaluatedValue = result;
      } else {
        evaluatedValue = null;
      }
    }
    
    const val = normalizeValue(locale, evaluatedValue);
    if (isNaN(val)) {
      setInputValue(lastFormattedValueRef.current);
      return;
    }

    const formattedValue = formatMoney(val, locale);
    setInputValue(formattedValue);
    
    if (val === lastValidValueRef.current)
        return
      
    onChange(val);

  };

  const handleFocus = () => {
    setSelected(true);
    const input = inputRef.current;

    if (input) {
      // Select all text when input is focused
      input.select();
    }
    
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  };

  return     <input
              ref={inputRef}  // Attach ref to the input element
              required={required}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              onFocus={handleFocus}
              style={{maxWidth: "100%"}}
              readOnly={isReadOnly}
              className={`${selected ? "field-selected" : "field-unselected"} ${className ?? "form-control-sm form-control "}`}
            />
};

export default MoneyInput;
